import { useState, useEffect, useRef, Fragment } from "react";
import { useParams, Link } from "react-router-dom";
import { getApiUrl } from "@/utils/api";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import axios from "axios";
import { Loader2, Menu, X, ChevronDown, Home, Tag, Mail, Info, Building2, FileText, Newspaper, Users, Briefcase, HelpCircle, Phone, MapPin, Star, BookOpen, Calendar, Image as ImageIcon, Folder, LogIn, LogOut, ShoppingBag, Heart, Bell } from "lucide-react";
import { NAV_ICON_MAP } from "@/utils/navIcons";
import { useShopperAuth } from "@/context/ShopperAuthContext";
import { useSiteStyles } from "@/hooks/useSiteStyles";
import { getAgentAuthed, getNavigation, getHeaderConfig } from "@/utils/chromeCache";
import { fetchPageComposition, getCachedPageComposition, isPageCompositionFresh } from "@/utils/pageCompositionCache";
import { PropertyCard } from "@/components/PropertyCard";
import ProgressiveImg from "@/components/ProgressiveImg";
import PageShell from "@/components/PageShell";
import EditableModuleOverlay from "@/components/EditableModuleOverlay";
import { OverflowNav } from "@/components/layout/OverflowNav";
import ProductsModule from "@/components/modules/ProductsModule";
import BasketModule from "@/components/modules/BasketModule";
import CheckoutModule from "@/components/modules/CheckoutModule";
import PropertyArticleModule from "@/components/modules/PropertyArticleModule";
import VerifyPhotoModule from "@/components/modules/VerifyPhotoModule";
import MilestonesDashboardModule from "@/components/modules/MilestonesDashboardModule";
import CapturePropertyFormModule from "@/components/modules/CapturePropertyFormModule";
import HousePricesModule from "@/components/modules/HousePricesModule";
import PlanningApplicationsModule from "@/components/modules/PlanningApplicationsModule";
import PlanningAppealsModule from "@/components/modules/PlanningAppealsModule";
import PricingTableModule from "@/components/modules/PricingTableModule";
import CardsGridModule from "@/components/modules/CardsGridModule";
import AdvertModule from "@/components/modules/AdvertModule";
import { resolveFontSize, resolveFontSizePx } from "@/utils/headingSize";
import { buildTextStyle } from "@/components/ui/TextStyleControls";
import { resolveImageUrl } from "@/utils/imageUrl";
import { getCtaIcon } from "@/components/cms/ctaIcons";
import RichHtml from "@/components/ui/RichHtml";
import ScriptEmbed from "@/components/cms/ScriptEmbed";
import { SignInModal } from "@/components/auth/SignInModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useCriticalImagePreload, useBackgroundImagePreload } from "@/hooks/useImagePreload";

const API = getApiUrl();

// Shared with ModuleConfigFields.jsx's LogoImageStyleControls — Header and
// Navigation module's logo <img> both read these `logo_image_*` config
// fields to size/border/shadow the logo without touching the surrounding
// company-name text styling (which uses the separate `logo_*` prefix).
const LOGO_IMAGE_SHADOW_CSS = {
  none: "",
  soft: "0 2px 8px rgba(0,0,0,0.12)",
  medium: "0 6px 16px rgba(0,0,0,0.18)",
  strong: "0 12px 28px rgba(0,0,0,0.3)",
};

function logoImageStyle(config, fallbackHeightPx) {
  const borderWidth = Number(config.logo_image_border_width || 0);
  return {
    height: `${Number(config.logo_image_height || fallbackHeightPx)}px`,
    borderRadius: `${Number(config.logo_image_border_radius || 0)}px`,
    border: borderWidth > 0
      ? `${borderWidth}px ${config.logo_image_border_style || "solid"} ${config.logo_image_border_color || "#e5e7eb"}`
      : undefined,
    boxShadow: LOGO_IMAGE_SHADOW_CSS[config.logo_image_shadow || "none"] || undefined,
  };
}

// Hero CTA link with hover state — supports per-instance color/font config.
// Empty values fall through to Site Styles via CSS variables.
const HERO_CTA_SIZE_PRESETS = {
  sm: { px: 16, py: 8 },
  md: { px: 24, py: 12 },
  lg: { px: 32, py: 16 },      // legacy default (px-8 py-4)
  xl: { px: 48, py: 20 },
};

function heroCtaPadding(config) {
  const preset = HERO_CTA_SIZE_PRESETS[config.cta_size] || HERO_CTA_SIZE_PRESETS.lg;
  const px = config.cta_padding_x != null && config.cta_padding_x !== "" ? Number(config.cta_padding_x) : preset.px;
  const py = config.cta_padding_y != null && config.cta_padding_y !== "" ? Number(config.cta_padding_y) : preset.py;
  return { padding: `${py}px ${px}px` };
}

function HeroCtaLink({ config }) {
  const [hover, setHover] = useState(false);
  const bg = hover
    ? (config.cta_hover_bg_color || "var(--button-hover-bg, var(--accent, #f3f4f6))")
    : (config.cta_bg_color || "var(--button-bg, var(--primary, #ffffff))");
  const fg = hover
    ? (config.cta_hover_text_color || "var(--button-hover-text, #2f4f4f)")
    : (config.cta_text_color || "var(--button-text, #2f4f4f)");
  const Icon = getCtaIcon(config.cta_icon);
  const iconPosition = config.cta_icon_position === "right" ? "right" : "left";
  const iconSize = Number(config.cta_icon_size) || 20;
  const iconGap = Number(config.cta_icon_gap ?? 8);
  return (
    <a
      href={config.cta_link}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="inline-flex items-center transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
      style={{
        ...heroCtaPadding(config),
        gap: Icon ? `${iconGap}px` : 0,
        flexDirection: iconPosition === "right" ? "row-reverse" : "row",
        fontFamily: config.cta_font_family || "var(--font-body, inherit)",
        fontSize: resolveFontSize(config.cta_font_size),
        fontWeight: config.cta_font_weight || "var(--body-weight, 600)",
        fontStyle: config.cta_font_style || "normal",
        textTransform: config.cta_text_transform || "none",
        letterSpacing: `${config.cta_letter_spacing || 0}px`,
        backgroundColor: bg,
        color: fg,
        border: `${config.cta_border_width || 0}px solid ${config.cta_border_color || "transparent"}`,
        borderRadius: `${config.cta_border_radius != null && config.cta_border_radius !== "" ? config.cta_border_radius : 8}px`,
      }}
    >
      {Icon && <Icon style={{ width: iconSize, height: iconSize, flexShrink: 0 }} aria-hidden="true" />}
      <span>{config.cta_text}</span>
    </a>
  );
}

// Module Renderer - renders different module types
export function ModuleRenderer({ module, moduleDetail }) {
  const config = { ...moduleDetail.config, ...module.config_override };

  switch (moduleDetail.type) {
    case "header":
      return <HeaderModule config={config} />;

    case "navigation":
      return <NavigationModule config={config} />;

    case "footer":
      return <FooterModule config={config} />;

    case "property_gallery":
      return <PropertyGalleryModule config={config} />;

    case "card":
      return <CardModule config={config} />;

    case "text": {
      const ctaTop = config.cta_position === "top" || config.cta_position === "both";
      const ctaBottom = config.cta_position === "both" || (!config.cta_position || config.cta_position === "bottom");
      return (
        <div>
          {ctaTop && (
            <div className="mb-6">
              <PropertiesCta config={config} />
            </div>
          )}
          <RichHtml className="prose max-w-none" html={config.content || ""} />
          {ctaBottom && (
            <div className="mt-6">
              <PropertiesCta config={config} />
            </div>
          )}
        </div>
      );
    }

    case "products":
      return <ProductsModule config={config} />;

    case "basket":
      return <BasketModule config={config} />;

    case "checkout":
      return <CheckoutModule config={config} />;

    case "property_article":
      return <PropertyArticleModule config={config} />;

    case "verify_photo":
      // `instance_id` passed through so the module keys its localStorage per
      // instance — a page with two verify_photo modules keeps state separate.
      return <VerifyPhotoModule config={{ ...config, instance_id: module.instance_id }} />;

    case "milestones":
      return <MilestonesDashboardModule config={config} />;

    case "capture_property_form":
      return <CapturePropertyFormModule config={config} />;

    case "house_prices_tool":
      return <HousePricesModule config={config} />;

    case "planning_applications_tool":
      return <PlanningApplicationsModule config={config} />;

    case "planning_appeals_tool":
      return <PlanningAppealsModule config={config} />;

    case "pricing_table":
      return <PricingTableModule config={config} />;

    case "cards_grid":
      return <CardsGridModule config={config} />;

    case "advert":
      return <AdvertModule config={config} />;

    case "hero": {
      const zoom = config.image_zoom || 100;
      const rotate = config.image_rotate || 0;
      const opacity = config.image_opacity || 100;
      const contrast = config.image_contrast || 100;
      const brightness = config.image_brightness || 100;
      const bannerHeight = config.banner_height || 500;

      // Dynamically load Google Font for CTA if not the default
      const ctaFontFamily = config.cta_font_family || "inherit";
      if (typeof document !== "undefined" && ctaFontFamily && ctaFontFamily !== "inherit") {
        const fontName = ctaFontFamily.split(",")[0].replace(/['"]/g, "").trim();
        const linkId = `hero-cta-font-${fontName.replace(/\s+/g, "-").toLowerCase()}`;
        if (fontName && !document.getElementById(linkId)) {
          const link = document.createElement("link");
          link.id = linkId;
          link.rel = "stylesheet";
          link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, "+")}:wght@300;400;500;600;700;800&display=swap`;
          document.head.appendChild(link);
        }
      }
      
      return (
        <div
          className="relative flex items-center justify-center overflow-hidden"
          style={{ minHeight: `${bannerHeight}px` }}
        >
          {/* Background Image Layer - vertically and horizontally centered */}
          {config.background_image && (
            <div
              className="absolute inset-0 z-0"
              style={{
                backgroundImage: `url(${config.background_image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
                transform: `scale(${zoom / 100}) rotate(${rotate}deg)`,
                opacity: opacity / 100,
                filter: `contrast(${contrast}%) brightness(${brightness}%)`,
                transformOrigin: 'center center'
              }}
            />
          )}
          
          {/* Fallback Gradient - Only if no background image. Uses Site Styles colours. */}
          {!config.background_image && (
            <div
              className="absolute inset-0 z-0"
              style={{
                background:
                  "linear-gradient(135deg, var(--primary-color, #2563eb), var(--accent-color, #7c3aed))",
              }}
            />
          )}
          
          {/* Content */}
          <div className="relative z-20 px-4 py-12 max-w-4xl mx-auto w-full">
            {config.title && (
              <h1
                className="text-4xl md:text-6xl font-bold mb-4 drop-shadow-lg leading-tight"
                style={{
                  color: config.title_color || "var(--hero-title-color, #ffffff)",
                  fontFamily: config.title_font_family || "var(--font-heading, inherit)",
                  ...buildTextStyle(config, "title_"),
                }}
              >
                {config.title}
                {config.title_part2 && (
                  <>
                    {" "}
                    <span style={{ color: config.title_part2_color || "var(--heading-accent-color, #e07b5d)" }}>
                      {config.title_part2}
                    </span>
                  </>
                )}
              </h1>
            )}
            {config.subtitle && (() => {
              const align = config.subtitle_text_align || "center";
              return (
                <p
                  className="text-lg md:text-2xl mb-8 drop-shadow-md leading-relaxed max-w-2xl"
                  style={{
                    color: config.subtitle_color || "var(--hero-subtitle-color, #ffffff)",
                    fontFamily: config.subtitle_font_family || "var(--font-body, inherit)",
                    marginLeft: align === "left" ? "0" : "auto",
                    marginRight: align === "right" ? "0" : "auto",
                    ...buildTextStyle(config, "subtitle_"),
                  }}
                >
                  {config.subtitle}
                </p>
              );
            })()}
            {/* CTA only shown when title is present */}
            {config.title && config.cta_text && config.cta_link && (
              <div
                className="flex"
                style={{
                  justifyContent: ({ left: "flex-start", center: "center", right: "flex-end" })[config.cta_align || "center"],
                }}
              >
                <HeroCtaLink config={config} />
              </div>
            )}
          </div>
        </div>
      );
    }

    case "image_gallery":
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(config.images || []).map((imgUrl, idx) => (
            <ProgressiveImg
              key={idx}
              src={imgUrl}
              alt={`Gallery ${idx + 1}`}
              className="w-full h-64 rounded-lg"
            />
          ))}
        </div>
      );

    case "contact_form":
      return (
        <div className="max-w-2xl mx-auto">
          <form className="space-y-4">
            {(config.form_fields || ["name", "email", "message"]).map((field) => (
              <div key={field} className="space-y-2">
                <label className="text-sm font-medium capitalize">{field}</label>
                {field === "message" ? (
                  <textarea
                    className="w-full min-h-32 px-3 py-2 border border-input rounded-md"
                    placeholder={`Enter your ${field}...`}
                  />
                ) : (
                  <input
                    type={field === "email" ? "email" : "text"}
                    className="w-full h-10 px-3 py-2 border border-input rounded-md"
                    placeholder={`Enter your ${field}...`}
                  />
                )}
              </div>
            ))}
            <button
              type="submit"
              className="w-full px-6 py-3 bg-primary text-white rounded hover:bg-primary/90 transition-colors"
            >
              Submit
            </button>
          </form>
        </div>
      );

    case "all_properties":
      return <AllPropertiesModule config={config} />;

    case "featured_properties":
      return <FeaturedPropertiesModule config={config} />;

    case "go_to": {
      const ctaFontSize = resolveFontSize(config.font_size);
      const ctaSize = config.cta_size || "md";
      const placement = config.cta_placement || "horizontal";
      // "card" (default) matches the legacy tall-card layout. "button-solid"
      // renders each CTA as a proper pill/rounded-rectangle CTA — same look
      // as the Hero primary button, so admins get one consistent CTA style
      // across the site. "button-outline" is the ghost variant.
      const ctaStyle = config.cta_style || "card";

      const paddingMap = { xs: "p-3", sm: "p-4", md: "p-6", lg: "p-8", xl: "p-10" };
      const titleSizeMap = { xs: "text-base", sm: "text-lg", md: "text-xl", lg: "text-2xl", xl: "text-3xl" };
      const btnPaddingMap = { xs: "px-4 py-2", sm: "px-5 py-2.5", md: "px-8 py-4", lg: "px-10 py-5", xl: "px-12 py-6" };
      const btnTextMap = { xs: "text-sm", sm: "text-base", md: "text-lg", lg: "text-xl", xl: "text-2xl" };
      // Fixed CTA card widths for the "spread" placement — keeps far-left
      // and far-right cards visually anchored to the outer edges without
      // becoming huge.
      const spreadWidthMap = { xs: "w-52", sm: "w-64", md: "w-80", lg: "w-96", xl: "w-[28rem]" };
      const ctaPadding = paddingMap[ctaSize] || "p-6";
      const titleSize = titleSizeMap[ctaSize] || "text-xl";
      const btnPadding = btnPaddingMap[ctaSize] || "px-8 py-4";
      const btnText = btnTextMap[ctaSize] || "text-lg";
      const spreadWidth = spreadWidthMap[ctaSize] || "w-80";

      const gridClassMap = {
        "horizontal": "grid-cols-1 md:grid-cols-2",
        "grid-2":     "grid-cols-1 sm:grid-cols-2",
        "grid-4":     "grid-cols-2 lg:grid-cols-4",
        "vertical":   "grid-cols-1 max-w-lg mx-auto",
      };
      const gridClass = gridClassMap[placement] || "grid-cols-1 md:grid-cols-2";

      // Build array, filter out CTAs with no title
      const ctas = [
        { title: config.cta1_title, description: config.cta1_description, slug: config.cta1_page_slug, icon: config.cta1_icon },
        { title: config.cta2_title, description: config.cta2_description, slug: config.cta2_page_slug, icon: config.cta2_icon },
        { title: config.cta3_title, description: config.cta3_description, slug: config.cta3_page_slug, icon: config.cta3_icon },
        { title: config.cta4_title, description: config.cta4_description, slug: config.cta4_page_slug, icon: config.cta4_icon },
      ].filter(cta => cta.title);

      if (ctas.length === 0) return null;

      // "spread" places the active CTAs at the far-left and far-right of the
      // container (with any middle CTAs distributed between them).
      const isSpread = placement === "spread";
      const isButton = ctaStyle === "button-solid" || ctaStyle === "button-outline";

      // Card variant retains the legacy look. Button variant renders each
      // CTA as a compact primary/ghost pill using the same Site Styles
      // variables the Hero CTA uses.
      const bgColor = config.cta_bg_color || "var(--button-bg, var(--primary, #2f4f4f))";
      const textColor = config.cta_text_color || (ctaStyle === "button-outline" ? bgColor : "var(--button-text, #ffffff)");
      const borderRadius = config.cta_border_radius != null && config.cta_border_radius !== "" ? config.cta_border_radius : 999;
      const borderWidth = ctaStyle === "button-outline" ? (config.cta_border_width || 2) : (config.cta_border_width || 0);
      const borderColor = config.cta_border_color || bgColor;

      const renderButtonCta = (cta, idx) => {
        const href = cta.slug === null ? "/"
          : cta.slug
            ? (cta.slug.startsWith('/') ? cta.slug : `/${cta.slug}`)
            : "#";
        const LeadingIcon = getCtaIcon(cta.icon);
        return (
          <a
            key={idx}
            href={href}
            className={`inline-flex items-center justify-center gap-2 font-semibold transition-all duration-200 shadow hover:shadow-lg hover:-translate-y-0.5 whitespace-nowrap ${btnPadding} ${btnText}`}
            style={{
              backgroundColor: ctaStyle === "button-outline" ? "transparent" : bgColor,
              color: textColor,
              borderRadius: `${borderRadius}px`,
              border: `${borderWidth}px solid ${borderColor}`,
              ...buildTextStyle(config, "title_"),
            }}
            data-testid={`go-to-cta-button-${idx}`}
          >
            {LeadingIcon && <LeadingIcon className="w-4 h-4" />}
            {cta.title}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        );
      };

      const renderCardCta = (cta, idx, extraCls = "") => {
        const href = cta.slug === null ? "/"
          : cta.slug
            ? (cta.slug.startsWith('/') ? cta.slug : `/${cta.slug}`)
            : "#";
        const LeadingIcon = getCtaIcon(cta.icon);
        return (
          <a key={idx} href={href} className={`group block ${ctaPadding} bg-white rounded-lg shadow-md hover:shadow-xl transition-all border-2 border-transparent hover:border-primary ${extraCls}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-3 min-w-0">
                {LeadingIcon && (
                  <span className="mt-1 text-primary shrink-0 group-hover:scale-110 transition-transform">
                    <LeadingIcon className="w-6 h-6" />
                  </span>
                )}
                <h3 className={`${titleSize} font-bold text-gray-900 group-hover:text-primary transition-colors`} style={buildTextStyle(config, "title_")}>
                  {cta.title}
                </h3>
              </div>
              <svg className="w-5 h-5 text-primary flex-shrink-0 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
            {cta.description && (
              <p className="text-gray-600 leading-relaxed text-sm" style={buildTextStyle(config, "description_")}>
                {cta.description}
              </p>
            )}
          </a>
        );
      };

      const renderCta = (cta, idx) => isButton ? renderButtonCta(cta, idx) : renderCardCta(cta, idx);

      return (
        <div className="bg-gradient-to-br from-slate-50 to-slate-100">
          <div className="container mx-auto px-4">
            {isSpread ? (
              <div
                className={`flex flex-wrap ${isButton ? "items-center" : "items-stretch"} justify-between gap-6`}
                style={{ fontSize: ctaFontSize }}
                data-testid="go-to-spread"
              >
                {ctas.map((cta, idx) => isButton ? renderButtonCta(cta, idx) : renderCardCta(cta, idx, `${spreadWidth} max-w-full`))}
              </div>
            ) : isButton ? (
              // Button style ignores card grid layout and just flows in a
              // centered wrap — matches how a "call-to-action bar" looks on
              // marketing pages.
              <div className="flex flex-wrap items-center justify-center gap-4" style={{ fontSize: ctaFontSize }}>
                {ctas.map(renderCta)}
              </div>
            ) : (
              <div className={`grid ${gridClass} gap-6 max-w-5xl mx-auto`} style={{ fontSize: ctaFontSize }}>
                {ctas.map(renderCta)}
              </div>
            )}
          </div>
        </div>
      );
    }

    default:
      return (
        <div className="text-center py-12 text-muted-foreground">
          Unknown module type: {moduleDetail.type}
        </div>
      );
  }
}

// Header Module - Simple header with logo, strap line, login, contact CTA
// ----------------------------------------------------------------------
// PropertyGalleryModule
// ----------------------------------------------------------------------
// Renders the image gallery of a specific property as a responsive grid.
// Photos come straight from the property record's `gallery` array — the
// --- Card module --------------------------------------------------------
// A single 25/75 horizontal card (image + content) OR — when the instance is
// configured with `data_source: "properties_from_url"` — a stack of such
// cards, one per property matching the URL query string. The instance config
// declares which URL keys are honored.
const ASPECT_PADDING = {
  "1:1": "100%",
  "4:3": "75%",
  "3:2": "66.6667%",
  "16:9": "56.25%",
};

function CardShell({ image, heading, description, type_text, location_text, price_text, cta_text, cta_link, image_aspect_ratio, image_rounded, headingStyle, headingClassName, "data-testid": testId }) {
  const aspectPad = ASPECT_PADDING[image_aspect_ratio] || ASPECT_PADDING["4:3"];
  const imgRounded = image_rounded ? "rounded-xl" : "rounded-none";
  return (
    <article
      className="flex flex-col md:flex-row items-stretch gap-4 md:gap-6 border bg-card p-3 md:p-4 rounded-lg"
      data-testid={testId}
    >
      <div className="md:w-1/4 flex-shrink-0">
        <div className={`relative w-full overflow-hidden bg-muted ${imgRounded}`} style={{ paddingTop: aspectPad }}>
          {image ? (
            <ProgressiveImg
              src={image}
              alt={heading || ""}
              className="!absolute inset-0 w-full h-full"
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-xs">No image</div>
          )}
        </div>
      </div>
      <div className="md:w-3/4 flex flex-col">
        {heading && (
          <h3 className={headingClassName || "font-semibold text-xl mb-2"} style={headingStyle}>
            {heading}
          </h3>
        )}
        {description && (
          <p className="text-sm text-muted-foreground mb-3 leading-relaxed">{description}</p>
        )}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm mb-4">
          {type_text && (<span><span className="text-muted-foreground">Type:</span> <span className="font-medium">{type_text}</span></span>)}
          {location_text && (<span><span className="text-muted-foreground">Location:</span> <span className="font-medium">{location_text}</span></span>)}
          {price_text && (<span><span className="text-muted-foreground">Price:</span> <span className="font-medium">{price_text}</span></span>)}
        </div>
        {cta_text && cta_link && (
          <div className="mt-auto">
            <Link
              to={cta_link}
              className="inline-flex items-center px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
            >
              {cta_text}
            </Link>
          </div>
        )}
      </div>
    </article>
  );
}

export function CardModule({ config }) {
  const dataSource = config?.data_source || "manual";

  // Manual single-card mode
  if (dataSource !== "properties_from_url") {
    return (
      <div className="my-6">
        <CardShell
          image={config?.image}
          heading={config?.heading}
          description={config?.description}
          type_text={config?.type_text}
          location_text={config?.location_text}
          price_text={config?.price_text}
          cta_text={config?.cta_text}
          cta_link={config?.cta_link}
          image_aspect_ratio={config?.image_aspect_ratio}
          image_rounded={config?.image_rounded !== false}
          headingStyle={{
            color: config?.heading_color || undefined,
            ...buildTextStyle(config, "heading_"),
          }}
          headingClassName="font-semibold mb-2"
          data-testid="card-module"
        />
      </div>
    );
  }

  // URL-filtered property list mode
  return <CardModulePropertyList config={config} />;
}

function CardModulePropertyList({ config }) {
  const [properties, setProperties] = useState(null); // null=loading, [] = none
  const browserLocation = window.location;
  const search = browserLocation.search || "";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const urlParams = new URLSearchParams(search);
        const apiParams = new URLSearchParams();
        // Map URL keys → /api/properties query params, honoring the per-key toggles.
        const mapping = [
          ["filter_location",      "location",      "location"],
          ["filter_property_type", "type",          "property_type"],
          ["filter_min_price",     "min_price",     "min_price"],
          ["filter_max_price",     "max_price",     "max_price"],
          ["filter_beds",          "beds",          "beds"],
          ["filter_baths",         "baths",         "baths"],
          ["filter_featured",      "featured",      "featured"],
        ];
        mapping.forEach(([toggle, urlKey, apiKey]) => {
          if (config?.[toggle] === false) return;
          const v = urlParams.get(urlKey);
          if (v !== null && v !== "") apiParams.set(apiKey, v);
        });
        const max = Number(config?.max_results) || 12;
        const url = `${getApiUrl()}/properties${apiParams.toString() ? `?${apiParams}` : ""}`;
        const resp = await fetch(url);
        const data = await resp.json();
        if (!cancelled) {
          setProperties(Array.isArray(data) ? data.slice(0, max) : []);
        }
      } catch (err) {
        console.error("CardModule property fetch failed:", err);
        if (!cancelled) setProperties([]);
      }
    })();
    return () => { cancelled = true; };
    // re-fetch when URL filters change OR when relevant toggles change
  }, [search, config?.filter_location, config?.filter_property_type, config?.filter_min_price, config?.filter_max_price, config?.filter_beds, config?.filter_baths, config?.filter_featured, config?.max_results]);

  const formatPrice = (p) => {
    if (p === null || p === undefined || p === "") return "";
    const n = typeof p === "number" ? p : parseInt(p, 10);
    if (!Number.isFinite(n)) return String(p);
    return n.toLocaleString("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 });
  };

  const heading = config?.heading;
  const emptyMessage = config?.empty_message || "No properties match the current filters.";

  return (
    <section className="my-8" data-testid="card-list-module">
      {heading && (
        <h2
          className="font-semibold mb-4"
          style={{
            color: config?.heading_color || undefined,
            ...buildTextStyle(config, "heading_"),
          }}
        >
          {heading}
        </h2>
      )}
      {properties === null && (
        <div className="text-sm text-muted-foreground">Loading…</div>
      )}
      {properties && properties.length === 0 && (
        <div className="text-sm text-muted-foreground border rounded-md p-6 text-center">
          {emptyMessage}
        </div>
      )}
      {properties && properties.length > 0 && (
        <div className="space-y-4">
          {properties.map((p) => (
            <CardShell
              key={p.property_id}
              image={p.main_image || (Array.isArray(p.gallery) && p.gallery[0] && (typeof p.gallery[0] === "object" ? p.gallery[0].media_url : p.gallery[0]))}
              heading={p.title}
              description={p.description ? String(p.description).slice(0, 220) + (String(p.description).length > 220 ? "…" : "") : ""}
              type_text={p.property_type}
              location_text={p.display_address}
              price_text={formatPrice(p.price)}
              cta_text={config?.cta_text || "More"}
              cta_link={`/property/${p.seo_slug || p.property_id}`}
              image_aspect_ratio={config?.image_aspect_ratio}
              image_rounded={config?.image_rounded !== false}
              data-testid={`card-property-${p.property_id}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// --- Property gallery module --------------------------------------------
// same array that property owners curate in the Property Admin Dashboard
// (`ImageUpload` component). The CMS module just chooses *which* property
// to show and how to lay out its images.
export function PropertyGalleryModule({ config }) {
  const [prop, setProp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  useLockBodyScroll(lightboxIdx !== null);

  useEffect(() => {
    if (!config?.property_id) {
      setProp(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${getApiUrl()}/properties/${config.property_id}`)
      .then((r) => { if (!cancelled) setProp(r.data); })
      .catch(() => { if (!cancelled) setProp(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [config?.property_id]);

  if (!config?.property_id) return null;
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 text-sm text-muted-foreground">
        Loading property gallery…
      </div>
    );
  }
  if (!prop) return null;

  // Gallery items are now `{media_url, media_type, caption, sort_order}` objects.
  // Sort by sort_order, then normalise to `{url, caption}` tuples for rendering.
  // Legacy string entries are still tolerated.
  const rawImages = Array.isArray(prop.gallery) ? prop.gallery : [];
  const normalised = rawImages
    .map((item, idx) => {
      if (item && typeof item === "object") {
        return {
          url: item.media_url || "",
          caption: item.caption || "",
          sort_order: Number.isFinite(item.sort_order) ? item.sort_order : idx,
        };
      }
      return { url: typeof item === "string" ? item : "", caption: "", sort_order: idx };
    })
    .filter((it) => it.url)
    .sort((a, b) => a.sort_order - b.sort_order);
  const images = normalised;
  const max = parseInt(config.max_images, 10);
  const sliced = Number.isFinite(max) && max > 0 ? images.slice(0, max) : images;
  if (sliced.length === 0) return null;

  const cols = parseInt(config.columns, 10) || 3;
  const gridColsMap = {
    1: "grid-cols-1",
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
  };
  const gapMap = { sm: "gap-2", md: "gap-4", lg: "gap-6" };
  const ratioMap = {
    "1:1": "aspect-square",
    "4:3": "aspect-[4/3]",
    "3:2": "aspect-[3/2]",
    "16:9": "aspect-video",
  };
  const rounded = config.rounded !== false ? "rounded-lg" : "";

  const headingStyle = {
    fontSize: resolveFontSize(config.heading_font_size),
    color: config.heading_color || undefined,
    ...buildTextStyle(config, "heading_"),
  };

  return (
    <section className="container mx-auto px-4 py-8">
      {config.heading_text && (
        <h2
          className="text-2xl font-bold mb-6"
          style={headingStyle}
          data-testid="property-gallery-heading"
        >
          {config.heading_text}
        </h2>
      )}
      <div
        className={`grid ${gridColsMap[cols] || gridColsMap[3]} ${gapMap[config.gap] || gapMap.md}`}
        data-testid="property-gallery-grid"
      >
        {sliced.map((item, i) => (
          <button
            key={`${item.url}-${i}`}
            type="button"
            className={`relative overflow-hidden ${rounded} bg-muted/30 ${config.lightbox !== false ? "cursor-zoom-in hover:opacity-95 transition" : "cursor-default"}`}
            onClick={() => config.lightbox !== false && setLightboxIdx(i)}
            data-testid={`property-gallery-image-${i}`}
            aria-label={`View image ${i + 1}`}
          >
            <div className={ratioMap[config.aspect_ratio] || ratioMap["4:3"]}>
              <ProgressiveImg
                src={resolveImageUrl(item.url)}
                alt={item.caption || `${prop.title || "Property"} image ${i + 1}`}
                className="w-full h-full"
                loading="lazy"
              />
            </div>
            {item.caption && (
              <span className="absolute bottom-2 left-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded truncate">
                {item.caption}
              </span>
            )}
            {i === 0 && config.show_featured_label && (
              <span className="absolute top-2 left-2 bg-primary text-primary-foreground text-xs font-semibold px-2 py-1 rounded">
                Featured
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Click-to-enlarge lightbox — opaque dark backdrop, single image. */}
      {lightboxIdx !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setLightboxIdx(null)}
          role="dialog"
          aria-modal="true"
          data-testid="property-gallery-lightbox"
        >
          <ProgressiveImg
            src={resolveImageUrl(sliced[lightboxIdx].url)}
            alt={sliced[lightboxIdx].caption || `Enlarged image ${lightboxIdx + 1}`}
            className="max-w-[92vw] max-h-[88vh] object-contain"
            fill={false}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            className="absolute top-4 right-4 text-white text-3xl leading-none focus:outline-none"
            onClick={() => setLightboxIdx(null)}
            aria-label="Close"
          >
            ×
          </button>
        </div>
      )}
    </section>
  );
}

export function HeaderModule({ config }) {
  // Fallback for empty OR broken per-instance logo → Company Logo set in
  // Admin → Site Styles. Site Styles is now the single source of truth for
  // brand imagery across header and footer. We also listen for
  // `slh:site-styles-updated` so a logo swap in the admin panel reflects
  // instantly on any mounted public surface — no hard refresh needed.
  // Company Logo is always read (not just when `logo_url` is empty) so
  // it's ready as an instant fallback if the per-instance image 404s —
  // e.g. a stale upload left over from an earlier admin edit. Read
  // straight from the already-fetched global context (SiteStylesProvider,
  // mounted once at the app root) instead of this header instance
  // re-fetching `/site-styles` itself on every mount — the context is
  // already reactive to admin edits (`updateStyles`), no extra listener
  // or network round trip needed.
  const { styles: siteStyles } = useSiteStyles();
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => { setLogoFailed(false); }, [config.logo_url]);
  const companyLogo = siteStyles?.companyLogo || "";

  const logoUrl = (config.logo_url && !logoFailed) ? config.logo_url : companyLogo;
  // Blank Logo Text box means "don't show a company name" — no fallback
  // default text, unlike the logo image which still falls back to the
  // Site Styles company logo.
  const logoText = config.logo_text || "";
  const logoFontSize = resolveFontSize(config.logo_font_size);
  const strapLine = config.strap_line || "";
  const strapLineFontSize = resolveFontSize(config.strap_line_font_size);
  // Resolution order for every colour: per-instance config → Site Styles CSS
  // variable → sensible hex fallback. This keeps a module instance free of
  // hardcoded brand colours by default and lets Site Styles flow through.
  const bgColor = config.bg_color || "var(--header-bg, #ffffff)";
  const textColor = config.text_color || "var(--header-text, var(--nav-text, #1a1a1a))";
  const linkColor = config.link_color || "var(--header-link, var(--header-text, var(--nav-text, #1a1a1a)))";
  const borderColor = config.border_color || "var(--header-border, var(--border-color, var(--border, #e5e5e5)))";
  const strapLineColor = config.strap_line_color || "var(--muted-foreground, #6b7280)";
  const showLogin = config.show_login || false;
  const loginLink = config.login_link || "/admin/login";

  return (
    <header
      className="border-b"
      style={{
        backgroundColor: bgColor,
        borderColor: borderColor,
        color: textColor,
      }}
    >
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
        {/* Logo Image + Text + Strap Line — all on the same row */}
        <div className="flex items-center gap-3 flex-wrap">
          <Link to="/" className="flex items-center gap-3">
            {logoUrl && <img src={logoUrl} alt={logoText} className="w-auto object-contain slh-no-img-style" style={logoImageStyle(config, 48)} onError={() => setLogoFailed(true)} />}
            {logoText && (
              <span
                className="font-medium"
                style={{
                  fontSize: logoFontSize,
                  color: textColor,
                  fontFamily: "var(--font-heading, 'Playfair Display', serif)",
                  ...buildTextStyle(config, "logo_"),
                }}
              >
                {logoText}
              </span>
            )}
          </Link>
          {strapLine && (
            <span
              className="hidden sm:inline-block pl-3 ml-1 border-l"
              style={{
                fontSize: strapLineFontSize,
                color: strapLineColor,
                borderColor: borderColor,
                fontFamily: "var(--font-body, inherit)",
                ...buildTextStyle(config, "strap_line_"),
              }}
              data-testid="header-strapline"
            >
              {strapLine}
            </span>
          )}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-3">
          {showLogin && (
            <a
              href={loginLink}
              className="text-sm transition-colors px-3 py-1.5 hover:opacity-80 hover:underline underline-offset-4"
              style={{ color: linkColor }}
            >
              Login
            </a>
          )}
        </div>
      </div>
    </header>
  );
}

// Navigation Module - Matches existing Navbar.jsx design
// Default icon mapping for common navigation link titles/paths.
// Returns a lucide-react component for the closest match, or a folder fallback.
function getNavIcon(item) {
  // 1) Explicit `icon` field set on the nav item wins.
  const chosen = (item.icon || "").trim();
  if (chosen && NAV_ICON_MAP[chosen]) return NAV_ICON_MAP[chosen];
  // 2) Fall back to a title/path keyword match so legacy items still get an
  //    appropriate icon without the admin having to configure each one.
  const title = (item.title || "").toLowerCase();
  const path = (item.path || "").toLowerCase();
  const probe = `${title} ${path}`;
  if (probe.includes("home") || path === "/") return Home;
  if (probe.includes("price") || probe.includes("pricing") || probe.includes("cost")) return Tag;
  if (probe.includes("contact")) return Mail;
  if (probe.includes("phone") || probe.includes("call")) return Phone;
  if (probe.includes("about")) return Info;
  if (probe.includes("propert") || probe.includes("listing") || probe.includes("homes")) return Building2;
  if (probe.includes("location") || probe.includes("address") || probe.includes("map")) return MapPin;
  if (probe.includes("agent") || probe.includes("team") || probe.includes("people") || probe.includes("staff")) return Users;
  if (probe.includes("career") || probe.includes("join")) return Briefcase;
  if (probe.includes("faq") || probe.includes("help")) return HelpCircle;
  if (probe.includes("blog") || probe.includes("news")) return Newspaper;
  if (probe.includes("gallery") || probe.includes("photo")) return ImageIcon;
  if (probe.includes("article") || probe.includes("guide") || probe.includes("resource")) return BookOpen;
  if (probe.includes("event") || probe.includes("calendar") || probe.includes("schedule")) return Calendar;
  if (probe.includes("review") || probe.includes("testimonial") || probe.includes("star")) return Star;
  if (probe.includes("page") || probe.includes("term") || probe.includes("privacy") || probe.includes("legal")) return FileText;
  if (probe.includes("login") || probe.includes("signin") || probe.includes("account")) return LogIn;
  return Folder;
}

// Normalize a nav path so it's always treated as an absolute route by React
// Router. Paths like "Properties" (no leading slash) would otherwise be
// interpreted as relative to the current location and produce broken URLs
// like /prices/Properties when navigating from /prices.
function absPath(path) {
  if (!path) return "/";
  if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("mailto:") || path.startsWith("tel:")) {
    return path;
  }
  return path.startsWith("/") ? path : `/${path}`;
}

// Renders a nav item as the right element for its `open_target`:
//   "same_tab" (default) → normal SPA <Link>
//   "new_tab"             → plain <a target="_blank">
//   "modal"               → <button> that opens the target page in an
//                            in-page modal overlay via `onOpenModal`
function NavAwareLink({ item, onOpenModal, className, style, onClick, children, ...rest }) {
  const target = item?.open_target || "same_tab";
  const href = absPath(item?.path || item?.url);
  if (target === "new_tab") {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className} style={style} onClick={onClick} {...rest}>
        {children}
      </a>
    );
  }
  if (target === "modal") {
    return (
      <button
        type="button"
        className={className}
        style={style}
        onClick={(e) => {
          onOpenModal && onOpenModal(item);
          onClick && onClick(e);
        }}
        {...rest}
      >
        {children}
      </button>
    );
  }
  return (
    <Link to={href} className={className} style={style} onClick={onClick} {...rest}>
      {children}
    </Link>
  );
}

// In-page modal overlay used by nav items configured with open_target="modal".
// Loads the target page inside an iframe so the visitor never leaves the
// current page context.
function NavPageModal({ item, onClose }) {
  return (
    <Dialog open={!!item} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-4xl w-[92vw] h-[85vh] p-0 flex flex-col overflow-hidden"
        data-testid="nav-modal-dialog"
      >
        <DialogHeader className="px-4 py-3 border-b border-border">
          <DialogTitle className="text-base">{item?.title}</DialogTitle>
        </DialogHeader>
        {item && (
          <iframe
            key={item.nav_id || item.path}
            src={absPath(item.path || item.url)}
            title={item.title}
            className="flex-1 w-full border-0"
            data-testid="nav-modal-iframe"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// Recognise "Login" / "Sign in" / "Account" nav items (either by title or by
// pointing at a known auth route) so the navigation can open the SSO modal
// instead of routing to the legacy agent login page.
function isSigninItem(item) {
  const title = (item?.title || "").toLowerCase().trim();
  const path = (item?.path || "").toLowerCase().trim();
  if (["login", "log in", "signin", "sign in", "sign-in", "account", "my account"].includes(title)) return true;
  if (["/admin/login", "/property-admin/login", "/login", "/signin", "/sign-in", "/account"].includes(path)) return true;
  return false;
}

export function NavigationModule({ config }) {
  // Optional logo + company name block shown on the left of this nav bar
  // (stacked: image on top, name underneath). Off by default so existing
  // instances that already carry a separate Header module above them don't
  // suddenly show a duplicate logo — toggled per-instance from the
  // Navigation module's admin config.
  const { styles: siteStyles } = useSiteStyles();
  const showNavLogo = config.show_logo === true;
  const navLogoUrl = config.logo_url || siteStyles?.companyLogo || "";
  // Blank Company Name box means "don't show a company name" — no fallback
  // to the global Site Styles nav text or a hardcoded default, unlike the
  // logo image which still falls back to the Site Styles company logo.
  const navLogoText = config.logo_text || "";
  const navLogoFontSize = resolveFontSize(config.logo_font_size, "h6");
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [navItems, setNavItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [headerConfig, setHeaderConfig] = useState({});
  // SSO sign-in modal now lives inside the navigation — the header no
  // longer renders its own "Sign in" button. Any nav item flagged as the
  // "Login" account entry (config.show_login) opens this modal instead of
  // routing to the legacy agent login page.
  const [signinOpen, setSigninOpen] = useState(false);
  const [signinError, setSigninError] = useState(null);
  // Modal-target nav items render as a <button> and open their page inside
  // this in-page overlay instead of navigating away.
  const [navModalItem, setNavModalItem] = useState(null);
  // If the visitor already has an active agent (property-admin) session, we
  // skip the sign-in modal and route Login clicks straight to their
  // dashboard so signed-in users never see a login prompt.
  const [agentAuthed, setAgentAuthed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getAgentAuthed().then((authed) => { if (!cancelled) setAgentAuthed(authed); });
    return () => { cancelled = true; };
  }, []);
  const openSignin = () => {
    if (agentAuthed) {
      window.location.href = "/property-admin/dashboard";
      return;
    }
    setSigninOpen(true);
  };
  const location = window.location;
  // Signed-in shopper drives the "My orders" quick link in the top-right —
  // hidden while auth is resolving so we don't flash it in and back out.
  const { shopper, signOut: shopperSignOut } = useShopperAuth();
  // Any signed-in state (shopper OR agent) flips the "Login" nav item into
  // a "Sign Out" action instead of opening the sign-in modal.
  const signedIn = !!shopper || agentAuthed;
  const handleSignOut = async () => {
    if (agentAuthed) {
      try { await axios.post(`${API}/property-admin/logout`, {}, { withCredentials: true }); } catch { /* noop */ }
    }
    if (shopper) {
      await shopperSignOut();
    }
    window.location.href = "/";
  };

  // Facebook/Google SSO failures redirect back to /?open_signin=1&fb_error=…
  // (or &google_error=…). Detect that once on mount, pop the modal with a
  // friendly banner, and scrub the params out of the URL so a refresh
  // doesn't re-trigger it.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("open_signin") !== "1") return;
    const err = params.get("fb_error") || "";
    const reason = params.get("fb_reason") || "";
    const googleErr = params.get("google_error") || "";
    const desc = params.get("fb_desc") || googleErr || "";
    if (err || reason || desc) {
      setSigninError({ error: err, reason, desc, provider: googleErr ? "google" : "facebook" });
    }
    setSigninOpen(true);
    ["open_signin", "fb_error", "fb_reason", "fb_desc", "google_error"].forEach((k) => params.delete(k));
    const search = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (search ? `?${search}` : "") + window.location.hash,
    );
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getNavigation(), getHeaderConfig()]).then(([items, headerCfg]) => {
      if (cancelled) return;
      setNavItems(items);
      setHeaderConfig(headerCfg);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const toggleMobileDropdown = (navId) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [navId]: !prev[navId]
    }));
  };

  // CTA from Header config (with nav config fallback). Site styles cascade
  // via CSS variables when no per-instance override is set.
  const ctaEnabled = config.cta_enabled !== false;
  const ctaText = config.cta_text || headerConfig.cta_text || "Request Viewing";
  const ctaLink = config.cta_link || headerConfig.cta_link || "/contact";
  const ctaBgColor = config.cta_bg_color || headerConfig.cta_bg_color || "var(--button-bg, #2f4f4f)";
  const ctaTextColor = config.cta_text_color || headerConfig.cta_text_color || "var(--button-text, #ffffff)";
  // Nav styling — defaults to Site Styles when not overridden per-instance.
  const bgColor = config.bg_color || "var(--nav-bg, #ffffff)";
  const textColor = config.text_color || "var(--nav-text, #1a1a1a)";
  // Hover colour pulls from Site Styles' Navigation → Link Hover (--nav-hover),
  // then Brand → Primary, then the dark slate fallback.
  const hoverColor = config.hover_color || "var(--nav-hover, var(--primary-color, #2f4f4f))";
  const fontSize = resolveFontSize(config.font_size);
  // Icon size must be a number (SVG attribute) — derived from the same level.
  const iconPx = resolveFontSizePx(config.font_size);
  // Per-instance link font family / weight / size / align — applied to every
  // nav link (main, Favourites, Alerts, Login, CTA-adjacent). Local
  // color/opacity still take priority so the hover / active states keep
  // working exactly as before.
  const linkStyle = buildTextStyle(config, "nav_link_");
  // Display mode: "both" (icon + label, default), "text" (label only), "icon"
  // (icon only). Applied module-wide, then optionally overridden per-item via
  // the nav item's own `display_mode` field ("inherit" or missing → uses this).
  const moduleDisplayMode = config.display_mode || "both";
  const itemMode = (item) => {
    const m = item?.display_mode;
    if (!m || m === "inherit") return moduleDisplayMode;
    return m;
  };
  const showIconFor = (item) => itemMode(item) !== "text";
  const showTextFor = (item) => itemMode(item) !== "icon";
  // Per-item visibility based on ANY signed-in state on the public site —
  // a shopper/buyer account (Google/Facebook/email) OR an agent session —
  // independent of Master Admin auth. The Login/Account item always stays
  // visible since it flips to "Sign Out" once signed in.
  const isVisibleFor = (item) => {
    if (isSigninItem(item)) return true;
    const signedIn = !!shopper || agentAuthed;
    if (item.visibility === "signed_in") return signedIn;
    if (item.visibility === "signed_out") return !signedIn;
    return true;
  };
  // Legacy module-level shortcuts still used by e.g. login/logout links.
  const showIcon = moduleDisplayMode !== "text";
  const showText = moduleDisplayMode !== "icon";
  // Active page indicator — bold label + a soft background pill in the
  // hover colour, rather than recolouring the text (which reads as an
  // error/alert on red/coral hover colours). Text colour stays textColor.
  const activeNavStyle = {
    fontWeight: 700,
    backgroundColor: `color-mix(in srgb, ${hoverColor} 15%, transparent)`,
    padding: "4px 10px",
    margin: "-4px -10px",
    borderRadius: "6px",
  };

  return (
    <nav
      className="relative z-30 w-full public-nav border-b"
      style={{
        backgroundColor: bgColor,
        borderColor: "var(--border-color, var(--border, rgba(0,0,0,0.05)))",
        fontFamily: "var(--font-body, inherit)",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className={showNavLogo ? "md:flex md:items-center md:justify-between md:gap-6" : ""}>
        {/* Top row: logo block OR strap line (left) + mobile hamburger (right).
            On desktop the hamburger is hidden but the logo/strap line still shows,
            aligned to the left where the hamburger sits on mobile. */}
        <div className={showNavLogo ? "flex items-center justify-between py-3 gap-4" : "flex items-center justify-between pt-4 pb-2 gap-4"}>
          {showNavLogo ? (
            <Link to="/" className="flex flex-col items-center gap-0.5 shrink-0" data-testid="navigation-logo">
              {navLogoUrl && (
                <img
                  src={resolveImageUrl(navLogoUrl)}
                  alt={navLogoText}
                  className="w-auto object-contain slh-no-img-style"
                  style={logoImageStyle(config, 40)}
                />
              )}
              {navLogoText && (
                <span
                  className="font-medium leading-none whitespace-nowrap"
                  style={{
                    fontSize: navLogoFontSize,
                    color: textColor,
                    fontFamily: "var(--font-heading, 'Playfair Display', serif)",
                    ...buildTextStyle(config, "logo_"),
                  }}
                  data-testid="navigation-logo-text"
                >
                  {navLogoText}
                </span>
              )}
            </Link>
          ) : config.strap_line ? (
            <span
              data-testid="navigation-strapline"
              className="truncate"
              style={{
                color: config.strap_line_color || "var(--muted-foreground, #6b7280)",
                fontSize: resolveFontSize(config.strap_line_font_size),
                fontFamily: "var(--font-body, inherit)",
                ...buildTextStyle(config, "strap_line_"),
              }}
            >
              {config.strap_line}
            </span>
          ) : (
            <span />
          )}

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            style={{ color: textColor }}
            onClick={() => setIsOpen(!isOpen)}
            data-testid="nav-mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Bottom Row: Navigation Links */}
        {(() => {
          const navLinkItems = [];
          if (!loading) {
            navItems.filter(item => !item.parent_id && item.show_in_top_nav !== false && isVisibleFor(item)).forEach((item) => {
              const isActive = location.pathname === item.path;
              const children = navItems.filter(child => child.parent_id === item.nav_id && child.show_in_top_nav !== false && isVisibleFor(child));
              const hasChildren = children.length > 0;
              const NavIcon = getNavIcon(item);

              if (hasChildren && config.enable_dropdown !== false) {
                navLinkItems.push({ key: item.nav_id, element: (
                  <div className="relative group flex items-center">
                    <NavAwareLink
                      item={item}
                      onOpenModal={setNavModalItem}
                      className="font-medium tracking-wide transition-all flex items-center gap-1.5 whitespace-nowrap"
                      style={{
                        fontSize,
                        ...linkStyle,
                        color: textColor,
                        opacity: isActive ? 1 : 0.75,
                        ...(isActive ? activeNavStyle : {}),
                      }}
                      aria-label={item.title}
                      title={!showTextFor(item) ? item.title : undefined}
                      onMouseEnter={(e) => { e.currentTarget.style.color = hoverColor; e.currentTarget.style.opacity = 1; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = textColor; e.currentTarget.style.opacity = isActive ? 1 : 0.75; }}
                    >
                      {showIconFor(item) && <NavIcon size={iconPx} className="shrink-0" />}
                      {showTextFor(item) && <span>{item.title}</span>}
                    </NavAwareLink>
                    <span
                      className="p-1 cursor-default"
                      style={{ color: textColor, opacity: 0.75 }}
                      aria-hidden="true"
                    >
                      <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                    </span>

                    {/* Dropdown — follows the same Site Styles colours as the
                        rest of the nav bar instead of a hardcoded white panel.
                        The outer wrapper uses top-full + pt-2 (padding, not
                        margin) so the gap above the panel is still part of its
                        own hoverable hit-box — a margin gap there is a dead
                        zone that drops the hover chain and closes the menu
                        before the cursor reaches it. */}
                    <div className="absolute top-full left-0 pt-2 min-w-[200px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                      <div
                        className="border shadow-lg rounded-md py-2"
                        style={{
                          backgroundColor: bgColor === "transparent" ? "var(--nav-bg, #ffffff)" : bgColor,
                          borderColor: "var(--border-color, var(--border, rgba(0,0,0,0.08)))",
                        }}
                      >
                        {children.map(child => {
                          const ChildIcon = getNavIcon(child);
                          return (
                            <NavAwareLink
                              key={child.nav_id}
                              item={child}
                              onOpenModal={setNavModalItem}
                              className="block px-4 py-2 text-sm transition-colors flex items-center gap-2 w-full text-left"
                              style={{ ...linkStyle, color: textColor, opacity: 0.85 }}
                              aria-label={child.title}
                              title={!showTextFor(child) ? child.title : undefined}
                              onMouseEnter={(e) => { e.currentTarget.style.color = hoverColor; e.currentTarget.style.opacity = 1; }}
                              onMouseLeave={(e) => { e.currentTarget.style.color = textColor; e.currentTarget.style.opacity = 0.85; }}
                            >
                              {showIconFor(child) && <ChildIcon size={14} className="shrink-0" />}
                              {showTextFor(child) && <span>{child.title}</span>}
                            </NavAwareLink>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) });
                return;
              }

              navLinkItems.push({ key: item.nav_id, element: (
                isSigninItem(item) ? (
                  <button
                    type="button"
                    onClick={signedIn ? handleSignOut : openSignin}
                    className="font-medium tracking-wide transition-all flex items-center gap-1.5"
                    style={{
                      fontSize,
                      ...linkStyle,
                      color: textColor,
                      opacity: isActive ? 1 : 0.75,
                      ...(isActive ? activeNavStyle : {}),
                    }}
                    aria-label={signedIn ? "Sign out" : item.title}
                    title={!showTextFor(item) ? (signedIn ? "Sign out" : item.title) : undefined}
                    onMouseEnter={(e) => { e.currentTarget.style.color = hoverColor; e.currentTarget.style.opacity = 1; }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = textColor;
                      e.currentTarget.style.opacity = isActive ? 1 : 0.75;
                    }}
                    data-testid={signedIn ? "nav-signout-btn" : "nav-signin-btn"}
                  >
                    {showIconFor(item) && (signedIn ? <LogOut size={iconPx} className="shrink-0" /> : <NavIcon size={iconPx} className="shrink-0" />)}
                    {showTextFor(item) && <span>{signedIn ? "Sign Out" : item.title}</span>}
                  </button>
                ) : (
                  <NavAwareLink
                    item={item}
                    onOpenModal={setNavModalItem}
                    className="font-medium tracking-wide transition-all flex items-center gap-1.5"
                    style={{
                      fontSize,
                      ...linkStyle,
                      color: textColor,
                      opacity: isActive ? 1 : 0.75,
                      ...(isActive ? activeNavStyle : {}),
                    }}
                    aria-label={item.title}
                    title={!showTextFor(item) ? item.title : undefined}
                    onMouseEnter={(e) => { e.currentTarget.style.color = hoverColor; e.currentTarget.style.opacity = 1; }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = textColor;
                      e.currentTarget.style.opacity = isActive ? 1 : 0.75;
                    }}
                  >
                    {showIconFor(item) && <NavIcon size={iconPx} className="shrink-0" />}
                    {showTextFor(item) && <span>{item.title}</span>}
                  </NavAwareLink>
                )
              ) });
            });
          }

          navLinkItems.push({ key: "favourites", element: (
            <Link
              to="/my-favourites"
              className="font-medium tracking-wide transition-all flex items-center gap-1.5"
              style={{ fontSize, ...linkStyle, color: textColor, opacity: 0.75 }}
              onMouseEnter={(e) => { e.currentTarget.style.color = hoverColor; e.currentTarget.style.opacity = 1; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = textColor; e.currentTarget.style.opacity = 0.75; }}
              data-testid="nav-my-favourites"
            >
              {showIcon && <Heart size={iconPx} className="shrink-0" />}
              {showText && <span>Favourites</span>}
            </Link>
          ) });

          if (shopper) {
            navLinkItems.push({ key: "alerts", element: (
              <Link
                to="/my-alerts"
                className="font-medium tracking-wide transition-all flex items-center gap-1.5"
                style={{ fontSize, ...linkStyle, color: textColor, opacity: 0.75 }}
                onMouseEnter={(e) => { e.currentTarget.style.color = hoverColor; e.currentTarget.style.opacity = 1; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = textColor; e.currentTarget.style.opacity = 0.75; }}
                data-testid="nav-my-alerts"
              >
                {showIcon && <Bell size={iconPx} className="shrink-0" />}
                {showText && <span>Alerts</span>}
              </Link>
            ) });
          }

          if (config.show_login) {
            navLinkItems.push({ key: "login", element: (
              <button
                type="button"
                onClick={openSignin}
                className="font-medium tracking-wide transition-all flex items-center gap-1.5"
                style={{
                  fontSize,
                  ...linkStyle,
                  color: textColor,
                  opacity: 0.75,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = hoverColor; e.currentTarget.style.opacity = 1; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = textColor; e.currentTarget.style.opacity = 0.75; }}
                data-testid="nav-signin-btn"
              >
                {showIcon && <LogIn size={iconPx} className="shrink-0" />}
                {showText && <span>Login</span>}
              </button>
            ) });
          }

          if (ctaEnabled) {
            navLinkItems.push({ key: "cta", element: (
              <Link to={ctaLink}>
                <button
                  className="px-6 py-2 font-medium rounded-none transition-all duration-200 shadow hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
                  style={{
                    backgroundColor: ctaBgColor,
                    color: ctaTextColor,
                    fontSize: fontSize
                  }}
                >
                  {ctaText}
                </button>
              </Link>
            ) });
          }

          return showNavLogo ? (
            <OverflowNav
              items={navLinkItems}
              className="hidden md:flex items-center gap-3"
              gap={12}
              textColor={textColor}
              bgColor={bgColor}
              fontSize={fontSize}
              linkStyle={linkStyle}
              data-testid="nav-overflow-row"
            />
          ) : (
            <div className="hidden md:flex items-center justify-center gap-8 pb-4">
              {navLinkItems.map(({ key, element }) => <Fragment key={key}>{element}</Fragment>)}
            </div>
          );
        })()}
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden border-t border-border/30 animate-fade-in">
            <div className="flex flex-col py-4 gap-3">
              {!loading && navItems.filter(item => !item.parent_id && item.show_in_top_nav !== false && isVisibleFor(item)).map((item) => {
                const isActive = location.pathname === item.path;
                const children = navItems.filter(child => child.parent_id === item.nav_id && child.show_in_top_nav !== false && isVisibleFor(child));
                const hasChildren = children.length > 0;
                const isDropdownOpen = openDropdowns[item.nav_id];
                const NavIcon = getNavIcon(item);

                if (hasChildren) {
                  return (
                    <div key={item.nav_id}>
                      <div className="flex items-center justify-between w-full">
                        <NavAwareLink
                          item={item}
                          onOpenModal={setNavModalItem}
                          className="text-base font-medium py-2 flex items-center gap-2 flex-1"
                          style={{ color: textColor, opacity: 0.7 }}
                          onClick={() => setIsOpen(false)}
                        >
                          {showIconFor(item) && <NavIcon size={16} className="shrink-0" />}
                          {showTextFor(item) && item.title}
                          {!showTextFor(item) && <span className="sr-only">{item.title}</span>}
                        </NavAwareLink>
                        <button
                          type="button"
                          className="py-2 px-2"
                          style={{ color: textColor, opacity: 0.7 }}
                          onClick={() => toggleMobileDropdown(item.nav_id)}
                          aria-label={`Toggle ${item.title} submenu`}
                        >
                          <ChevronDown
                            size={16}
                            className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                          />
                        </button>
                      </div>
                      {isDropdownOpen && (
                        <div className="pl-4 mt-2 space-y-2">
                          {children.map(child => {
                            const ChildIcon = getNavIcon(child);
                            return (
                              <NavAwareLink
                                key={child.nav_id}
                                item={child}
                                onOpenModal={setNavModalItem}
                                className="text-sm font-medium py-2 flex items-center gap-2 w-full text-left"
                                style={{
                                  color: textColor,
                                  opacity: location.pathname === child.path ? 1 : 0.6
                                }}
                                onClick={() => setIsOpen(false)}
                              >
                                <ChildIcon size={14} className="shrink-0" />
                                <span>{child.title}</span>
                              </NavAwareLink>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  isSigninItem(item) ? (
                    <button
                      key={item.nav_id}
                      type="button"
                      onClick={() => { setIsOpen(false); signedIn ? handleSignOut() : openSignin(); }}
                      className="text-base font-medium py-2 flex items-center gap-2 text-left"
                      style={{
                        color: textColor,
                        opacity: isActive ? 1 : 0.7
                      }}
                      aria-label={signedIn ? "Sign out" : item.title}
                      title={!showTextFor(item) ? (signedIn ? "Sign out" : item.title) : undefined}
                      data-testid={signedIn ? "nav-signout-btn-mobile" : "nav-signin-btn-mobile"}
                    >
                      {showIconFor(item) && (signedIn ? <LogOut size={16} className="shrink-0" /> : <NavIcon size={16} className="shrink-0" />)}
                      {showTextFor(item) && <span>{signedIn ? "Sign Out" : item.title}</span>}
                    </button>
                  ) : (
                  <NavAwareLink
                    key={item.nav_id}
                    item={item}
                    onOpenModal={setNavModalItem}
                    className="text-base font-medium py-2 flex items-center gap-2 w-full text-left"
                    style={{
                      color: textColor,
                      opacity: isActive ? 1 : 0.7
                    }}
                    aria-label={item.title}
                    title={!showTextFor(item) ? item.title : undefined}
                    onClick={() => setIsOpen(false)}
                  >
                    {showIconFor(item) && <NavIcon size={16} className="shrink-0" />}
                    {showTextFor(item) && <span>{item.title}</span>}
                  </NavAwareLink>
                  )
                );
              })}

              {/* Mobile: Favourites — mirrors the desktop link so shoppers can
                  reach their saved properties without opening the desktop nav. */}
              <Link
                to="/my-favourites"
                className="text-base font-medium py-2 flex items-center gap-2"
                style={{ color: textColor, opacity: 0.7 }}
                onClick={() => setIsOpen(false)}
                data-testid="mobile-nav-my-favourites"
              >
                <Heart size={16} className="shrink-0" />
                <span>Favourites</span>
              </Link>

              {/* Mobile: Alerts — only for signed-in shoppers (matches desktop) */}
              {shopper && (
                <Link
                  to="/my-alerts"
                  className="text-base font-medium py-2 flex items-center gap-2"
                  style={{ color: textColor, opacity: 0.7 }}
                  onClick={() => setIsOpen(false)}
                  data-testid="mobile-nav-my-alerts"
                >
                  <Bell size={16} className="shrink-0" />
                  <span>Alerts</span>
                </Link>
              )}

              {/* Mobile Login — same modal-open behaviour as desktop */}
              {config.show_login && (
                <button
                  type="button"
                  onClick={() => { setIsOpen(false); openSignin(); }}
                  className="text-base font-medium py-2 text-left flex items-center gap-2"
                  style={{ color: textColor, opacity: 0.7 }}
                  data-testid="nav-signin-btn-mobile"
                >
                  <LogIn size={16} className="shrink-0" />
                  <span>Login</span>
                </button>
              )}

              {/* Mobile CTA */}
              {ctaEnabled && (
                <Link to={ctaLink} onClick={() => setIsOpen(false)}>
                  <button
                    className="w-full mt-2 px-6 py-2 font-medium rounded-none"
                    style={{ backgroundColor: ctaBgColor, color: ctaTextColor }}
                  >
                    {ctaText}
                  </button>
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
      <SignInModal open={signinOpen} onOpenChange={setSigninOpen} initialError={signinError} />
      <NavPageModal item={navModalItem} onClose={() => setNavModalItem(null)} />
    </nav>
  );
}

// Footer Module - Matches existing Footer.jsx design
export function FooterModule({ config }) {
  const [navFooterLinks, setNavFooterLinks] = useState([]);
  // Modal-target footer links open their page inside this in-page overlay.
  const [navModalItem, setNavModalItem] = useState(null);
  const { shopper } = useShopperAuth();
  const [agentAuthed, setAgentAuthed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getAgentAuthed().then((authed) => { if (!cancelled) setAgentAuthed(authed); });
    return () => { cancelled = true; };
  }, []);

  // If the footer instance doesn't specify its own logo, or that image
  // 404s (a stale upload), fall back to the Company Logo set in Admin
  // Dashboard → Site Styles. Site Styles is the single source of truth for
  // brand imagery — read straight from the already-fetched global context
  // (SiteStylesProvider, mounted once at the app root) instead of each
  // header/footer instance re-fetching `/site-styles` itself on mount.
  const { styles: siteStyles } = useSiteStyles();
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => { setLogoFailed(false); }, [config.logo_url]);
  const companyLogo = siteStyles?.companyLogo || "";

  // Pull navigation items flagged "show_in_footer" and render them as
  // additional footer links (merged with manually-configured config.links).
  // Visibility (signed-in/out) is applied at render time below so it stays
  // correct if the shopper's auth state resolves after this fetch.
  useEffect(() => {
    let cancelled = false;
    getNavigation().then((items) => {
      if (cancelled) return;
      setNavFooterLinks(
        items
          .filter((n) => n.show_in_footer === true && n.enabled !== false)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((n) => ({ label: n.title, url: n.path, open_target: n.open_target, nav_id: n.nav_id, visibility: n.visibility }))
      );
    });
    return () => { cancelled = true; };
  }, []);

  const visibleNavFooterLinks = navFooterLinks.filter((l) => {
    const signedIn = !!shopper || agentAuthed;
    if (l.visibility === "signed_in") return signedIn;
    if (l.visibility === "signed_out") return !signedIn;
    return true;
  });

  const logoUrl = (config.logo_url && !logoFailed) ? config.logo_url : (companyLogo || "https://customer-assets.emergentagent.com/job_realty-gallery/artifacts/zzc7uqc2_Screenshot_20260119_035617_Chrome.ico");
  const companyName = config.company_name || "SellLocalHomes";
  const tagline = config.tagline || "Your trusted partner in selling your home.";
  // Per-instance config wins; otherwise inherit the global Site Styles for the
  // footer (set in Admin Dashboard → Site Styles).
  const bgColor = config.bg_color || "var(--footer-bg, #2f4f4f)";
  const textColor = config.text_color || "var(--footer-text, #ffffff)";
  const currentYear = new Date().getFullYear();
  const year = config.copyright_year || currentYear;
  const showAdminLink = config.show_admin_link !== false;

  // Typography styles for footer links + non-link copy (set in Module Library).
  const linkStyle = {
    fontFamily: config.link_font_family || "var(--font-body, inherit)",
    fontSize: config.link_font_size ? resolveFontSize(config.link_font_size) : undefined,
    fontWeight: config.link_font_weight || undefined,
    fontStyle: config.link_font_style || undefined,
  };
  const copyStyle = {
    fontFamily: config.copy_font_family || "var(--font-body, inherit)",
    fontWeight: config.copy_font_weight || undefined,
    fontStyle: config.copy_font_style || undefined,
    textTransform: config.copy_text_transform || undefined,
  };

  // Merge nav-driven footer links with any manually-configured links.
  // De-dup by URL so the same destination doesn't appear twice.
  const manualLinks = config.links || [];
  const mergedLinks = [...manualLinks];
  for (const navLink of visibleNavFooterLinks) {
    if (!mergedLinks.some((l) => l.url === navLink.url)) {
      mergedLinks.push(navLink);
    }
  }

  // Partner / accreditation logos — each entry is {image_url, link_url, alt}.
  // Rendered as a horizontal strip below the main footer content. Every image
  // opens its `link_url` in a new tab when clicked (falls back to non-clickable
  // if no URL is set). `partner_logos_size_px` controls the uniform height.
  const partnerLogos = Array.isArray(config.partner_logos) ? config.partner_logos : [];
  const partnerLogoSize = Number(config.partner_logos_size_px) || 40;

  return (
    <footer style={{ backgroundColor: bgColor, color: textColor, fontFamily: "var(--font-body, inherit)" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-8">
        {/* Logo + Company Name */}
        <div className="mb-6">
          <Link to="/" className="flex items-center gap-3">
            <img src={logoUrl} alt={companyName} className="h-10 w-10 object-contain slh-no-img-style" onError={() => setLogoFailed(true)} />
            <span
              className="text-xl font-medium"
              style={{
                color: textColor,
                fontFamily: "var(--font-heading, 'Playfair Display', serif)",
                ...buildTextStyle(config, "company_name_"),
              }}
            >
              {companyName}
            </span>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          {/* About/Tagline */}
          <div className="space-y-3">
            <p
              className="text-xs leading-relaxed"
              style={{
                color: textColor,
                opacity: 0.7,
                ...buildTextStyle(config, "tagline_"),
              }}
            >
              {tagline}
            </p>

            {/* Partner / accreditation logos — configured in the Module Library.
                Rendered directly below the strapline (tagline). Each logo can
                carry a URL; clicking opens it in a new tab. */}
            {partnerLogos.length > 0 && (
              <div
                className="flex flex-wrap items-center gap-6"
                data-testid="footer-partner-logos"
              >
                {partnerLogos.map((logo, idx) => {
                  if (!logo || !logo.image_url) return null;
                  const img = (
                    <img
                      src={logo.image_url}
                      alt={logo.alt || `Partner logo ${idx + 1}`}
                      style={{ height: `${partnerLogoSize}px`, width: "auto", objectFit: "contain" }}
                      className="opacity-80 hover:opacity-100 transition-opacity"
                      data-testid={`footer-partner-logo-img-${idx}`}
                    />
                  );
                  return logo.link_url ? (
                    <a
                      key={idx}
                      href={logo.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={logo.alt || logo.link_url}
                      data-testid={`footer-partner-logo-link-${idx}`}
                    >
                      {img}
                    </a>
                  ) : (
                    <span key={idx} data-testid={`footer-partner-logo-static-${idx}`}>{img}</span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Links + Copyright */}
          <div className="space-y-3 text-right">
            <div className="flex flex-col gap-1 items-end" data-testid="footer-links">
              {mergedLinks.map((link, idx) => (
                <NavAwareLink
                  key={`${link.url}-${idx}`}
                  item={link}
                  onOpenModal={setNavModalItem}
                  className="text-xs transition-colors"
                  style={{ color: textColor, opacity: 0.5 }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = 0.7}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = 0.5}
                >
                  {link.label}
                </NavAwareLink>
              ))}
            </div>
            
            <p className="text-xs" style={{ color: textColor, opacity: 0.5 }}>
              © {year} {companyName}. All rights reserved.
            </p>
            
            {showAdminLink && (
              <Link
                to="/admin"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs transition-colors"
                style={{ color: textColor, opacity: 0.3 }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = 0.5}
                onMouseLeave={(e) => e.currentTarget.style.opacity = 0.3}
              >
                Admin Dashboard
              </Link>
            )}
          </div>
        </div>
      </div>
      <NavPageModal item={navModalItem} onClose={() => setNavModalItem(null)} />
    </footer>
  );
}

// All Properties Module
// Shared CTA renderer used by AllProperties / FeaturedProperties.
function PropertiesCta({ config = {} }) {
  if (!config.cta_enabled || !config.cta_text) return null;
  const ctaAlign = config.cta_align || "center";
  const justifyMap = { left: "flex-start", center: "center", right: "flex-end" };
  return (
    <div
      className="flex"
      style={{ justifyContent: justifyMap[ctaAlign] || "center" }}
      data-testid="properties-cta"
    >
      <Link to={config.cta_link || "/"}>
        <button
          type="button"
          className="px-6 py-2 font-medium transition-all duration-200 shadow hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
          style={{
            backgroundColor: config.cta_bg_color || "var(--button-bg, #2f4f4f)",
            color: config.cta_text_color || "var(--button-text, #ffffff)",
            borderRadius: `${config.cta_border_radius || 8}px`,
          }}
        >
          {config.cta_text}
        </button>
      </Link>
    </div>
  );
}

// Shared heading renderer for AllProperties / FeaturedProperties (heading +
// optional subheading, no CTA — CTA is rendered separately so the admin can
// control its position above OR below the property grid).
function PropertiesHeader({ config = {}, fallbackTitle, fallbackSub }) {
  const headingText = config.heading_text || fallbackTitle;
  const subText = config.subheading_text !== undefined ? config.subheading_text : fallbackSub;
  const align = config.heading_align || "center";

  const headingStyle = {
    fontFamily: config.heading_font_family || "var(--font-heading, inherit)",
    fontSize: resolveFontSize(config.heading_font_size),
    fontWeight: config.heading_font_weight || "700",
    fontStyle: config.heading_font_style || "normal",
    textTransform: config.heading_text_transform || "none",
    color: config.heading_color || "var(--heading-color, #1a1a1a)",
    textAlign: align,
  };
  const subStyle = {
    color: config.subheading_color || "hsl(var(--muted-foreground))",
    fontSize: resolveFontSize(config.subheading_font_size),
    textAlign: align === "justify" ? "left" : align,
  };

  return (
    <div className="mb-12" style={{ textAlign: align === "justify" ? "left" : align }}>
      {headingText && (
        <h2 className="mb-2" style={headingStyle}>
          {headingText}
        </h2>
      )}
      {subText && <p style={subStyle}>{subText}</p>}
    </div>
  );
}

function AllPropertiesModule({ config = {} }) {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      // Filter enabled properties only
      const enabled = response.data.filter(p => p.enabled !== false);
      setProperties(enabled);
    } catch (error) {
      console.error("Failed to fetch properties:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const ctaTop = config.cta_position !== "bottom";
  return (
    <div>
      <PropertiesHeader
        config={config}
        fallbackTitle="All Properties"
        fallbackSub="Browse all our available properties"
      />
      {ctaTop && (
        <div className="mb-8">
          <PropertiesCta config={config} />
        </div>
      )}
      {properties.length === 0 ? (
        <p className="text-center text-muted-foreground">No properties available at the moment.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties.map((property) => (
            <PropertyCard key={property.property_id} property={property} />
          ))}
        </div>
      )}
      {!ctaTop && (
        <div className="mt-10">
          <PropertiesCta config={config} />
        </div>
      )}
    </div>
  );
}

// Featured Properties Module
function FeaturedPropertiesModule({ config = {} }) {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      // Filter featured and enabled properties only
      const featured = response.data.filter(p => p.featured === true && p.enabled !== false);
      setProperties(featured);
    } catch (error) {
      console.error("Failed to fetch properties:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const ctaTop = config.cta_position !== "bottom";
  return (
    <div>
      <PropertiesHeader
        config={config}
        fallbackTitle="Featured Properties"
        fallbackSub="Discover our handpicked featured listings"
      />
      {ctaTop && (
        <div className="mb-8">
          <PropertiesCta config={config} />
        </div>
      )}
      {properties.length === 0 ? (
        <p className="text-center text-muted-foreground">No featured properties at the moment.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {properties.map((property) => (
            <PropertyCard key={property.property_id} property={property} />
          ))}
        </div>
      )}
      {!ctaTop && (
        <div className="mt-10">
          <PropertiesCta config={config} />
        </div>
      )}
    </div>
  );
}

// Main Modular Page Component
/**
 * Extracts image URLs that should be preloaded for the given page composition
 * and (when relevant) prefetches the images of the *next* route the user is
 * most likely to visit.
 *
 * - Critical (`<link rel="preload">`): every hero background + the header
 *   logo. These paint above the fold on first-view, so telling the browser
 *   about them via <head> shaves hundreds of ms off Largest Contentful Paint.
 * - Background prefetch (`Image().src`): warmed silently during idle time.
 *     - On /home    → warm the /properties card thumbnails so tapping
 *                     "All Properties" feels instant.
 *     - On /properties → warm the FIRST card image ahead of the fold so the
 *                        grid never pops in.
 */
function ImagePreloader({ composition, slug }) {
  // 1. Collect above-the-fold URLs from the composition itself.
  const criticalUrls = [];
  (composition?.modules || []).forEach((m) => {
    const cfg = { ...(m.module_detail?.config || {}), ...(m.config_override || {}) };
    const type = m.module_detail?.type || m.module_type;
    if (type === "hero" && cfg.background_image) criticalUrls.push(cfg.background_image);
    if (type === "header" && cfg.logo_url) criticalUrls.push(cfg.logo_url);
    // Per-instance background image on a wrapping module also paints early.
    if (cfg.bg_image) criticalUrls.push(cfg.bg_image);
  });

  // 2. Fetch the property listing once so we can silently warm its thumbnails.
  //    We only do this on /home + /properties per the product decision to
  //    keep prefetch focused (see chat: "Home + Properties listing").
  const [nextRouteUrls, setNextRouteUrls] = useState([]);
  useEffect(() => {
    if (slug !== "home" && slug !== "properties") {
      setNextRouteUrls([]);
      return;
    }
    let alive = true;
    axios
      .get(`${API}/properties`, { params: { limit: 12 } })
      .then((res) => {
        if (!alive) return;
        const items = Array.isArray(res.data) ? res.data : res.data?.items || res.data?.properties || [];
        const urls = items
          .map((p) => p?.main_image)
          .filter(Boolean);
        setNextRouteUrls(urls);
      })
      .catch(() => {
        // Prefetch is best-effort — a failed listing fetch here is silent.
        if (alive) setNextRouteUrls([]);
      });
    return () => { alive = false; };
  }, [slug]);

  const criticalHead = useCriticalImagePreload(criticalUrls);
  useBackgroundImagePreload(nextRouteUrls);
  return null; // side-effect only
}

export default function ModularPage({ fixedSlug } = {}) {
  const { slug: routeSlug } = useParams();
  const [composition, setComposition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use the explicit `fixedSlug` (passed by static routes like /properties → "properties"
  // or / → "home"). Otherwise fall back to the React Router :slug param (custom pages).
  // We deliberately do NOT read window.location.pathname here so that when a modal
  // route is layered on top of a listing (background-location pattern), the
  // listing keeps fetching its own composition instead of the foreground URL.
  const resolvedSlug = fixedSlug || routeSlug || "home";
  // Tracks the slug a still-in-flight fetchComposition() was called for, so
  // a slow response for a page the visitor has since navigated away from
  // can't overwrite the new page's composition when it finally resolves.
  const latestSlugRef = useRef(resolvedSlug);
  useEffect(() => { latestSlugRef.current = resolvedSlug; }, [resolvedSlug]);

  useEffect(() => {
    fetchComposition();
  }, [resolvedSlug]);

  // Edit Mode: re-pull this page's own composition after a module instance
  // save so the just-edited section reflects its new config immediately.
  useEffect(() => {
    const onInstanceUpdated = () => fetchComposition(true);
    window.addEventListener("slh:module-instance-updated", onInstanceUpdated);
    return () => window.removeEventListener("slh:module-instance-updated", onInstanceUpdated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedSlug]);

  // First home-page visit in the session → scroll to top. Browsers restore
  // scroll on back/forward for a same-session in-app navigation, which can
  // land users mid-page on the home hero. Session flag ensures we only do
  // this once per browser session; subsequent home visits behave normally.
  useEffect(() => {
    if (resolvedSlug !== "home") return;
    try {
      const KEY = "slh_home_scrolled_top_v1";
      if (window.sessionStorage.getItem(KEY)) return;
      window.sessionStorage.setItem(KEY, "1");
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch { /* sessionStorage unavailable → skip silently */ }
  }, [resolvedSlug]);

  const fetchComposition = async (force = false) => {
    const requestedSlug = resolvedSlug;
    const cachedValue = getCachedPageComposition(requestedSlug);
    const isFresh = isPageCompositionFresh(requestedSlug);
    if (cachedValue && !force) {
      // Show the cached page instantly — no spinner flash on a revisit.
      setComposition(cachedValue);
      setError(null);
      setLoading(false);
      if (isFresh) return; // still within TTL, no need to revalidate yet
    } else {
      setLoading(true);
      // Reset previous state so a successful fetch isn't masked by a stale
      // error from a prior route (e.g., user navigating from a 404 page).
      setError(null);
      if (!cachedValue) setComposition(null);
    }
    try {
      const data = await fetchPageComposition(requestedSlug, { force });
      if (latestSlugRef.current !== requestedSlug) return; // navigated away — drop this stale response
      setComposition(data);
      setError(null);
    } catch (error) {
      if (latestSlugRef.current !== requestedSlug) return;
      console.error("Failed to fetch page composition:", error);
      // Page is gated behind an agent login — send the visitor to sign in,
      // then bounce them back to this exact page once authenticated.
      if (error.response?.status === 401 && error.response?.data?.detail === "agent_login_required") {
        const redirectTarget = window.location.pathname + window.location.search;
        window.location.href = `/property-admin/login?redirect=${encodeURIComponent(redirectTarget)}`;
        return;
      }
      // Had a cached page to fall back on (background revalidation failed)
      // — keep showing it rather than replacing it with an error screen.
      if (!cachedValue) {
        setError(error.response?.status === 404 ? "Page not found" : "Failed to load page");
      }
    } finally {
      if (latestSlugRef.current === requestedSlug) setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageShell>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">{error}</h1>
            <a href="/" className="text-primary hover:underline">
              Return to home
            </a>
          </div>
        </div>
      </PageShell>
    );
  }

  if (!composition) {
    return null;
  }

  // Sort modules by order
  const sortedModules = [...(composition.modules || [])].sort((a, b) => a.order - b.order);

  // PageShell no longer renders global header/nav/footer — modular pages
  // render every attached module in declared order.
  const bodyModules = sortedModules;

  // Full-bleed modules manage their own width/position
  const FULL_BLEED_TYPES = new Set(['hero', 'go_to', 'header', 'navigation', 'footer']);

  return (
    <PageShell>
      <ImagePreloader composition={composition} slug={resolvedSlug} />
      <div className="min-h-screen">
        {bodyModules.length === 0 ? (
          <div className="text-center py-24 max-w-7xl mx-auto px-6">
            <h1 className="text-4xl font-bold mb-4">{composition.page_title}</h1>
            <p className="text-muted-foreground">This page has no content modules yet.</p>
          </div>
        ) : (
          bodyModules.map((module, idx) => {
            const moduleType = module.module_detail?.type || module.module_type;
            const isFullBleed = FULL_BLEED_TYPES.has(moduleType);
            const key = module.instance_id || module.id || `${moduleType}-${idx}`;

            // Optional per-instance breathing room — admins can add extra
            // gap above or below any module. Config lives on either the
            // instance override or the module's own config (whichever the
            // editor wrote to). Sanitised to 0-256px so a mis-typed value
            // can't blow out the layout. When the admin hasn't set a value
            // yet (`undefined` / `null`), we apply a sensible 10 px default
            // so modules don't butt up against each other.
            const instanceCfg = { ...(module.module_detail?.config || {}), ...(module.config_override || {}) };
            const clampGap = (v) => {
              if (v === undefined || v === null || v === "") return 10;
              const n = Number(v);
              if (!Number.isFinite(n) || n < 0) return 0;
              return Math.min(Math.round(n), 256);
            };
            const bgStyle = {};
            if (instanceCfg.bg_color) bgStyle.backgroundColor = instanceCfg.bg_color;
            if (instanceCfg.bg_image) {
              bgStyle.backgroundImage = `url("${instanceCfg.bg_image}")`;
              bgStyle.backgroundSize = instanceCfg.bg_size || "cover";
              bgStyle.backgroundPosition = instanceCfg.bg_position || "center";
              bgStyle.backgroundRepeat = instanceCfg.bg_repeat || "no-repeat";
            }
            const gapStyle = {
              paddingTop: clampGap(instanceCfg.top_gap) || undefined,
              paddingBottom: clampGap(instanceCfg.bottom_gap) || undefined,
              ...bgStyle,
            };

            if (isFullBleed) {
              return (
                <div key={key} style={gapStyle} data-testid={`module-wrapper-${moduleType}`}>
                  <EditableModuleOverlay instanceId={module.instance_id} moduleType={moduleType}>
                    <ModuleRenderer module={module} moduleDetail={module.module_detail} />
                  </EditableModuleOverlay>
                  <ScriptEmbed html={instanceCfg.custom_script} />
                </div>
              );
            }

            // For contained modules the background needs to extend
            // edge-to-edge, so we split: outer div carries the background,
            // inner div keeps the max-width constraint. When there's no
            // background we short-circuit and skip the extra wrapper so we
            // don't affect the existing DOM tree.
            if (instanceCfg.bg_color || instanceCfg.bg_image) {
              return (
                <div key={key} style={gapStyle} data-testid={`module-wrapper-${moduleType}`}>
                  <div className="slh-page-placeholder px-6">
                    <EditableModuleOverlay instanceId={module.instance_id} moduleType={moduleType}>
                      <ModuleRenderer module={module} moduleDetail={module.module_detail} />
                    </EditableModuleOverlay>
                  </div>
                  <ScriptEmbed html={instanceCfg.custom_script} />
                </div>
              );
            }

            return (
              <div
                key={key}
                className="slh-page-placeholder px-6"
                style={gapStyle}
                data-testid={`module-wrapper-${moduleType}`}
              >
                <EditableModuleOverlay instanceId={module.instance_id} moduleType={moduleType}>
                  <ModuleRenderer module={module} moduleDetail={module.module_detail} />
                </EditableModuleOverlay>
                <ScriptEmbed html={instanceCfg.custom_script} />
              </div>
            );
          })
        )}
      </div>
    </PageShell>
  );
}
